import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export const AuthModal = ({ open, onClose }) => {
  const { setUser } = useAuth();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const endpoint = mode === "login" ? "/auth/login" : "/auth/register";
      const payload = mode === "login"
        ? { email: form.email, password: form.password }
        : { email: form.email, password: form.password, name: form.name };
      const { data } = await api.post(endpoint, payload);
      setUser(data);
      if (mode === "login") {
        const isModerator = data?.role === "moderator" || data?.role === "admin";
        toast.success(isModerator ? "Welcome back, moderator." : "Welcome back!");
      } else {
        toast.success("Account created. Start reporting.");
      }
      onClose();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="border-[#1E293F] bg-[#0F1424] text-white sm:max-w-md" data-testid="auth-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl font-black tracking-tight">
            {mode === "login" ? "Sign in to " : "Join "}
            <span className="text-[#00F0FF]">2K Case Files</span>
          </DialogTitle>
          <p className="text-sm text-[#94A3B8]">Report cheaters. Vet the wall. Get them banned.</p>
        </DialogHeader>

        <Button
          data-testid="google-login-btn"
          onClick={googleLogin}
          variant="outline"
          className="w-full border-[#1E293F] bg-[#141B2D] text-white hover:bg-[#1A233A] hover:text-white"
        >
          <img src="https://www.svgrepo.com/show/475656/google-color.svg" alt="" className="mr-2 h-4 w-4" />
          Continue with Google
        </Button>

        <div className="flex items-center gap-3 text-xs text-[#64748B]">
          <div className="h-px flex-1 bg-[#1E293F]" /> OR <div className="h-px flex-1 bg-[#1E293F]" />
        </div>

        <form onSubmit={submit} className="space-y-3">
          {mode === "register" && (
            <div>
              <Label className="text-[#94A3B8]">Display name</Label>
              <Input data-testid="auth-name-input" value={form.name} onChange={set("name")} required
                className="mt-1 border-[#1E293F] bg-[#141B2D] text-white" placeholder="e.g. HoopsWatcher" />
            </div>
          )}
          <div>
            <Label className="text-[#94A3B8]">Email</Label>
            <Input data-testid="auth-email-input" type="email" value={form.email} onChange={set("email")} required
              className="mt-1 border-[#1E293F] bg-[#141B2D] text-white" placeholder="you@email.com" />
          </div>
          <div>
            <Label className="text-[#94A3B8]">Password</Label>
            <Input data-testid="auth-password-input" type="password" value={form.password} onChange={set("password")} required
              className="mt-1 border-[#1E293F] bg-[#141B2D] text-white" placeholder="••••••••" />
          </div>

          {error && <p data-testid="auth-error" className="text-sm text-[#FF2A55]">{error}</p>}

          <Button data-testid="auth-submit-btn" type="submit" disabled={busy}
            className="w-full bg-[#00F0FF] font-bold text-[#090C15] hover:bg-[#00F0FF]/90">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {mode === "login" ? "Sign In" : "Create Account"}
          </Button>
        </form>

        <p className="text-center text-sm text-[#94A3B8]">
          {mode === "login" ? "No account yet?" : "Already registered?"}{" "}
          <button
            data-testid="auth-toggle-mode"
            onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
            className="font-semibold text-[#00F0FF] hover:underline"
          >
            {mode === "login" ? "Create one" : "Sign in"}
          </button>
        </p>
      </DialogContent>
    </Dialog>
  );
};
